import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../components/common/ui/card";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Users,
  Briefcase,
  FileText,
  Brain,
  MessageCircle,
  FileCheck,
  Clock,
  PlusIcon,
} from "lucide-react";
import UsersTable from "./UsersTable";
import UserActivityDashboard from "./UserActivityDashboard";
import api from "../services/api";
import TokenManagement from "./pages/TokenManagement";
import AdminConfigPanel from "./admin/AdminConfigPanel";
import AdminInsights from "./admin/AdminInsights";
import AdminPromptsPanel from "./admin/AdminPromptsPanel";
import AdminLedgerPanel from "./admin/AdminLedgerPanel";
import AdminAssessmentPanel from "./admin/AdminAssessmentPanel";
import AdminUserActivityPanel from "./admin/AdminUserActivityPanel";

const PipelineStageCard = ({
  title,
  value = 0,
  subtitle,
  icon: Icon,
  trend,
  className,
}) => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <h3 className="text-2xl font-bold mt-1 text-gray-900">
            {value || 0}
          </h3>
          {subtitle && (
            <p className={`text-sm mt-1 ${className || "text-gray-500"}`}>
              {subtitle}
            </p>
          )}
          {trend !== undefined && (
            <p
              className={`text-sm mt-2 ${
                Number(trend) > 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              {Number(trend) > 0 ? "↑" : "↓"} {Math.abs(trend)}% from last month
            </p>
          )}
        </div>
        <div className="p-3 bg-orange-100 rounded-full">
          <Icon className="h-6 w-6 text-orange-600" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  useEffect(()=>{
    console.log("userRole",userRole)
  },[userRole])
  const [activeTabForUserRole, setActiveTabForUserRole] = useState("activity");

  useEffect(() => {
    const user = localStorage.getItem("user");
    const userObj = JSON.parse(user);
    const userRole = userObj.role;

    if (userRole === "user" && users.length > 0) {
      const user = users.find((user) => user._id === userObj.id);
      console.log(user, "user");
      setUserRole(userRole);
      setSelectedUser(user);
    }
    else if(userRole==="admin") {
      setUserRole("admin")
    }
  }, [users, selectedUser]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    users: { total: 0, active: 0 },
    jobs: { total: 0, active: 0 },
    pipeline: {
      totalCandidates: 0,
      screening: 0,
      shortlisted: 0,
      interviewed: 0,
      offered: 0,
      joined: 0,
    },
    jobPipeline: [],
    tokenUsage: [],
  });

  useEffect(() => {
    fetchDashboardData();
    fetchUsers();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/dashboard/stats");
      if (response.data) {
        setDashboardStats(response.data);
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Failed to load dashboard statistics");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get("/admin/users");
      setUsers(response.data.users || []);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Error fetching users");
    }
  };

  const handleViewActivity = (user) => {
    if (!user?._id) {
      console.error("Invalid user data:", user);
      return;
    }
    setSelectedUser(user);
  };

  const handleBack = () => {
    setSelectedUser(null);
  };

  const handleUserDelete = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      fetchUsers();
    } catch (err) {
      console.error("Error deleting user:", err);
    }
  }
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  const getPercentage = (value, total) => {
    if (!total || !value) return 0;
    return ((value / total) * 100).toFixed(1);
  };

  const pipelineData = [
    {
      stage: "Total Candidates",
      value: dashboardStats.pipeline?.totalCandidates || 0,
    },
    {
      stage: "Resume Screening",
      value: dashboardStats.pipeline?.screening || 0,
    },
    { stage: "Shortlisted", value: dashboardStats.pipeline?.shortlisted || 0 },
    { stage: "Interviewed", value: dashboardStats.pipeline?.interviewed || 0 },
    { stage: "Offered", value: dashboardStats.pipeline?.offered || 0 },
    { stage: "Joined", value: dashboardStats.pipeline?.joined || 0 },
  ];

  return (
    <div className="p-6">
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-600 rounded-lg">
          {error}
        </div>
      )}

      {!selectedUser ? (
        <>
          <div className="mb-6 border-b">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab("overview")}
                className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "overview"
                    ? "border-orange-500 text-orange-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab("users")}
                className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "users"
                    ? "border-orange-500 text-orange-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Users
              </button>
              <button
                onClick={() => setActiveTab("tokens")}
                className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "tokens"
                    ? "border-orange-500 text-orange-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Token Management
              </button>
              {[
                { key: "config",      label: "System Config" },
                { key: "prompts",     label: "Interview Prompts" },
                { key: "ledger",      label: "Cost Ledger" },
                { key: "assessments",    label: "Assessment Management" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === key
                      ? "border-orange-500 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>
          </div>

          {activeTab === "config"      && <AdminConfigPanel />}
          {activeTab === "prompts"     && <AdminPromptsPanel />}
          {activeTab === "ledger"      && <AdminLedgerPanel />}
          {activeTab === "assessments" && <AdminAssessmentPanel />}

          {/* Redesigned overview — coherent funnel, real KPIs, ROI, attention list */}
          {activeTab === "overview" && <AdminInsights />}

          {/* Legacy overview block kept inert (false &&) for reference */}
          {false && activeTab === "overview" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                <PipelineStageCard
                  title="Total Users"
                  value={dashboardStats.users?.total}
                  subtitle={`${dashboardStats.users?.active || 0} active users`}
                  icon={Users}
                />
                <PipelineStageCard
                  title="Resume Screening"
                  value={dashboardStats.pipeline?.screening}
                  subtitle={`${getPercentage(
                    dashboardStats.pipeline?.screening,
                    dashboardStats.pipeline?.totalCandidates
                  )}% completed`}
                  icon={FileText}
                  className="text-orange-600"
                />
                <PipelineStageCard
                  title="Pre-Screening"
                  value={dashboardStats.pipeline?.shortlisted}
                  subtitle={`${getPercentage(
                    dashboardStats.pipeline?.shortlisted,
                    dashboardStats.pipeline?.screening
                  )}% pass rate`}
                  icon={Brain}
                  className="text-orange-600"
                />
                <PipelineStageCard
                  title="Interviews"
                  value={dashboardStats.pipeline?.interviewed}
                  subtitle={`${getPercentage(
                    dashboardStats.pipeline?.interviewed,
                    dashboardStats.pipeline?.shortlisted
                  )}% conversion`}
                  icon={MessageCircle}
                  className="text-orange-600"
                />
                <PipelineStageCard
                  title="Offers"
                  value={dashboardStats.pipeline?.offered}
                  subtitle={`${getPercentage(
                    dashboardStats.pipeline?.offered,
                    dashboardStats.pipeline?.interviewed
                  )}% success rate`}
                  icon={Briefcase}
                  className="text-orange-600"
                />
                <PipelineStageCard
                  title="Joined"
                  value={dashboardStats.pipeline?.joined}
                  subtitle={`${getPercentage(
                    dashboardStats.pipeline?.joined,
                    dashboardStats.pipeline?.offered
                  )}% acceptance`}
                  icon={FileCheck}
                  className="text-orange-600"
                />
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Overall Hiring Pipeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={pipelineData}
                        layout="vertical"
                        margin={{ top: 20, right: 30, left: 120, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="stage" type="category" />
                        <Tooltip />
                        <Bar dataKey="value" fill="#f97316" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Job Pipeline Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dashboardStats.jobPipeline}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar
                            dataKey="candidates"
                            fill="#fed7aa"
                            name="Total Candidates"
                          />
                          <Bar
                            dataKey="shortlisted"
                            fill="#fdba74"
                            name="Shortlisted"
                          />
                          <Bar
                            dataKey="interviewed"
                            fill="#fb923c"
                            name="Interviewed"
                          />
                          <Bar
                            dataKey="selected"
                            fill="#f97316"
                            name="Selected"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* <Card>
                  <CardHeader>
                    <CardTitle>Token Usage Trends</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={dashboardStats.tokenUsage}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="used"
                            stroke="#f97316"
                            name="Tokens Used"
                          />
                          <Line
                            type="monotone"
                            dataKey="allocated"
                            stroke="#84cc16"
                            name="Tokens Allocated"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card> */}
              </div>
            </div>
          )}

          {activeTab === "users" && Array.isArray(users) && (
            <UsersTable
              users={users}
              onEdit={() => {}}
              onDelete={handleUserDelete}
              onViewActivity={handleViewActivity}
              onRefresh={fetchUsers}
            />
          )}

          {activeTab === "tokens" && <TokenManagement />}
        </>
      ) : (
        <div>
          <div className="mb-4 flex items-center">
            {userRole === "admin" && (
              <button
                onClick={handleBack}
                className="px-4 py-2 bg-orange-100 hover:bg-orange-200 rounded-lg flex items-center text-orange-600"
              >
                <span>← Back to Users</span>
              </button>
            )}
            {userRole === "user" ? (
           <div className="border-b border-gray-200">
           <nav className="flex space-x-4 pr-4 pl-2" aria-label="Tabs">
             <h2
               onClick={() => {
                 setActiveTabForUserRole("activity");
               }}
               className={`text-xl font-semibold cursor-pointer px-4 py-2 border-b-2 transition-colors duration-200
                 ${
                   activeTabForUserRole === "activity"
                     ? "border-orange-500 text-orange-600 bg-orange-50"
                     : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                 }
               `}
             >
               Activity Dashboard
             </h2>
         
             <h2
               onClick={() => {
                 setActiveTabForUserRole("tokens");
               }}
               className={`text-xl font-semibold cursor-pointer px-4 py-2 border-b-2 transition-colors duration-200
                 ${
                   activeTabForUserRole === "tokens"
                     ? "border-orange-500 text-orange-600 bg-orange-50"
                     : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                 }
               `}
             >
               Token Management
             </h2>
           </nav>
         </div>
            ) : (
              <h2 className="ml-4 text-xl font-semibold text-gray-900">
                Activity Dashboard: {selectedUser.name}
              </h2>
            )}
          </div>

          {userRole === "user" ? (
            <>
              {activeTabForUserRole === "activity" ? (
                <UserActivityDashboard
                  userId={selectedUser._id}
                  userName={selectedUser.name}
                  key={selectedUser._id}
                />
              ) : (
                <TokenManagement
                  user={selectedUser}
                  onSuccess={handleBack}
                  onCancel={handleBack}
                  userRole={userRole}
                />
              )}
            </>
          ) : (
            <UserActivityDashboard
              userId={selectedUser._id}
              userName={selectedUser.name}
              key={selectedUser._id}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
